/**
 * Manifest builder for the Library file-system view.
 *
 * Maps backend `listFolders` + `listDocuments` results into the
 * FileSystemItem[] shape consumed by the Pierre FileTree component, plus a
 * `metaByPath` sidecar that carries per-item display data (document id,
 * folder id, status fields, etc.) keyed by the FINAL path used in the tree.
 */

import type {
  FileSystemFolderItem,
  FileSystemFileItem,
  FileSystemItem,
} from "@/ui/composed/file-system/file-system";
import type { FailureClass } from "@cvx/lib/failureClasses";

// ---------------------------------------------------------------------------
// Input types — shaped from what listFolders / listDocuments actually return
// ---------------------------------------------------------------------------

export interface LibraryFolder {
  _id: string;
  name: string;
  /** Morgan folder path: leading slash, no trailing slash. e.g. "/Bank/2026" */
  path: string;
  parentFolderId?: string;
  connectionId?: string;
  /** Presence signals the folder is synced from an external source. */
  remoteItemId?: string;
}

type ProcessingStatus =
  | "pending"
  | "extracting"
  | "extracted"
  | "classifying"
  | "classified"
  | "indexing"
  | "indexed"
  | "failed"
  | undefined;

export interface LibraryDocument {
  _id: string;
  _creationTime: number;
  jobId: string;
  storageId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  category: string;
  uploadedBy: string;
  notes?: string;
  uploaderName: string;
  url: string | null;
  folderId?: string;
  folderPath?: string;
  extractionStatus: ProcessingStatus;
  classificationStatus: ProcessingStatus;
  indexingStatus: ProcessingStatus;
  documentSubCategory?: string;
  documentType?: string;
  metadataConfidence?: number;
  classificationConfidence?: number;
  practitionerVerified?: boolean;
  lastProcessedAt?: number;
  extractionError?: string;
  classificationError?: string;
  indexingError?: string;
  extractionFailureClass?: FailureClass;
  classificationFailureClass?: FailureClass;
  indexingFailureClass?: FailureClass;
}

// ---------------------------------------------------------------------------
// Output type — meta sidecar
// ---------------------------------------------------------------------------

export interface LibraryItemMeta {
  /** Present for file items. */
  documentId?: string;
  /** Present for folder items. */
  folderId?: string;
  /** Document category (for file items). */
  category?: string;
  /** True when the item originates from an external sync connection. */
  isSynced: boolean;
  /** Flat-mode only: human-readable folder location, e.g. "/Bank/2026" or "Library root". */
  location?: string;
  // --- Per-doc display fields ---
  uploaderName?: string;
  documentType?: string;
  documentSubCategory?: string;
  classificationConfidence?: number;
  metadataConfidence?: number;
  practitionerVerified?: boolean;
  extractionStatus?: ProcessingStatus;
  classificationStatus?: ProcessingStatus;
  indexingStatus?: ProcessingStatus;
  extractionError?: string;
  classificationError?: string;
  indexingError?: string;
  extractionFailureClass?: FailureClass;
  classificationFailureClass?: FailureClass;
  indexingFailureClass?: FailureClass;
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface BuildLibraryItemsOptions {
  /**
   * When true, ignore folderPath entirely: all docs appear at top level.
   * `meta.location` carries the original folderPath (or "Library root").
   * The folders array is ignored.
   */
  flat?: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Insert a suffix ` (xxxx)` (last 4 chars of the doc _id) before the
 * extension, producing a disambiguation path. Folder paths (trailing
 * slash) get the suffix before the slash — `"Tax/"` → `"Tax (abcd)/"` —
 * so the result is still a valid folder path, never a phantom child.
 */
function disambiguatePath(path: string, id: string): string {
  const suffix = id.slice(-4);
  if (path.endsWith("/")) {
    return `${path.slice(0, -1)} (${suffix})/`;
  }
  const dotIndex = path.lastIndexOf(".");
  if (dotIndex === -1 || dotIndex === 0) {
    // No extension (or hidden file starting with dot but no second dot)
    return `${path} (${suffix})`;
  }
  const base = path.slice(0, dotIndex);
  const ext = path.slice(dotIndex);
  return `${base} (${suffix})${ext}`;
}

/**
 * Ensure `path` does not collide with any key already in `seen`.
 * If it does, disambiguate using `id`. Returns the final (possibly modified)
 * path. Mutates `seen`.
 */
function claimPath(path: string, id: string, seen: Set<string>): string {
  if (!seen.has(path)) {
    seen.add(path);
    return path;
  }
  // Try to find an unused path by appending the id suffix
  let candidate = disambiguatePath(path, id);
  // In the very unlikely case the candidate is also taken, keep extending
  let attempts = 0;
  while (seen.has(candidate) && attempts < 100) {
    candidate = disambiguatePath(candidate, id + attempts.toString());
    attempts++;
  }
  seen.add(candidate);
  return candidate;
}

/** Path a document would claim before de-duplication, given its folder. */
function documentBasePath(doc: LibraryDocument, flat: boolean): string {
  if (flat || !doc.folderPath) {
    // Ignore folderPath, place at root
    return doc.fileName;
  }
  const folderPrefix = doc.folderPath.startsWith("/")
    ? doc.folderPath.slice(1)
    : doc.folderPath;
  // Ensure no double slash
  const prefix = folderPrefix.endsWith("/") ? folderPrefix : folderPrefix + "/";
  return prefix + doc.fileName;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildLibraryItems(
  folders: LibraryFolder[],
  documents: LibraryDocument[],
  options?: BuildLibraryItemsOptions,
): { items: FileSystemItem[]; metaByPath: Map<string, LibraryItemMeta> } {
  const flat = options?.flat ?? false;
  const items: FileSystemItem[] = [];
  const metaByPath = new Map<string, LibraryItemMeta>();
  const seen = new Set<string>();

  if (!flat) {
    // --- Folders ---
    for (const folder of folders) {
      // Strip leading slash, add trailing slash
      const rawPath = folder.path.startsWith("/")
        ? folder.path.slice(1)
        : folder.path;
      const basePath = rawPath.endsWith("/") ? rawPath : rawPath + "/";

      const finalPath = claimPath(basePath, folder._id, seen);

      const folderItem: FileSystemFolderItem = {
        kind: "folder",
        path: finalPath,
        name: folder.name,
      };
      items.push(folderItem);

      metaByPath.set(finalPath, {
        folderId: folder._id,
        isSynced: !!folder.remoteItemId,
      });
    }
  }

  // --- Documents ---
  for (const doc of documents) {
    const basePath = documentBasePath(doc, flat);

    const finalPath = claimPath(basePath, doc._id, seen);

    const fileItem: FileSystemFileItem = {
      kind: "file",
      path: finalPath,
      name: doc.fileName,
      contentType: doc.fileType,
      size: doc.fileSize,
      createdAt: new Date(doc._creationTime).toISOString(),
      ...(doc.url ? { url: doc.url } : {}),
      // Images render their Convex file URL directly as the thumbnail.
      ...(doc.url && doc.fileType.startsWith("image/")
        ? { previewImageUrl: doc.url }
        : {}),
      // PDFs declare one lazily-rendered page so thumbnail tiles request a
      // first-page preview via loadPreviewImageUrl (see pdfThumbnails.ts)
      // without showing a pager.
      ...(doc.fileType === "application/pdf" ? { previewPageCount: 1 } : {}),
    };
    items.push(fileItem);

    const meta: LibraryItemMeta = {
      documentId: doc._id,
      category: doc.category,
      isSynced: false,
      uploaderName: doc.uploaderName,
      documentType: doc.documentType,
      documentSubCategory: doc.documentSubCategory,
      classificationConfidence: doc.classificationConfidence,
      metadataConfidence: doc.metadataConfidence,
      practitionerVerified: doc.practitionerVerified,
      extractionStatus: doc.extractionStatus,
      classificationStatus: doc.classificationStatus,
      indexingStatus: doc.indexingStatus,
      extractionError: doc.extractionError,
      classificationError: doc.classificationError,
      indexingError: doc.indexingError,
      extractionFailureClass: doc.extractionFailureClass,
      classificationFailureClass: doc.classificationFailureClass,
      indexingFailureClass: doc.indexingFailureClass,
    };

    if (flat) {
      meta.location = doc.folderPath ?? "Library root";
    }

    metaByPath.set(finalPath, meta);
  }

  return { items, metaByPath };
}
